import React from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, TrendingUp, TrendingDown, ArrowRight } from "lucide-react";
import { Tile } from "../Primitives";
import { AsOfChip } from "./EndingInventoryCard";
import { syn } from "../../lib/origin.jsx";
import { demandOutlookFor } from "../../lib/dataPilot";
import { useRole } from "../../lib/role.jsx";
import { formatPeso, formatNumber, cn } from "../../lib/utils";

function OpenLink({ to, label, testid }) {
  const navigate = useNavigate();
  const { canAccess } = useRole();
  if (!canAccess(to)) return null;
  return (
    <button data-testid={testid} onClick={() => navigate(to)}
            className="text-[11px] text-primary hover:underline inline-flex items-center gap-1">
      {label} <ArrowRight size={10} />
    </button>
  );
}

export function EndingInventorySnapshot({ valuation }) {
  return (
    <Tile className="col-span-12 md:col-span-6" testid="brief-ending-inventory" origin={valuation.originProps}
          title={<span className="text-[11px] font-semibold tracking-widest uppercase text-muted inline-flex items-center gap-2"><Wallet size={12} className="text-primary" /> Ending Inventory snapshot</span>}>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div data-testid="brief-ending-inventory-value" className="font-mono-tabular text-3xl font-semibold text-primary leading-none">
            {formatPeso(valuation.total, { compact: true })}
          </div>
          <div className="mt-2"><AsOfChip valuation={valuation} testid="brief-ending-inventory-as-of" /></div>
        </div>
        <OpenLink to="/inventory" label="Open valuation" testid="brief-open-valuation" />
      </div>
    </Tile>
  );
}

export function DemandOutlookTile({ branchId }) {
  const o = demandOutlookFor(branchId);
  const up = o.delta >= 0;
  return (
    <Tile className="col-span-12 md:col-span-6" testid="brief-demand-outlook" origin={syn("POS demand history & 30-day forecast")}
          title={<span className="text-[11px] font-semibold tracking-widest uppercase text-muted inline-flex items-center gap-2"><TrendingUp size={12} className="text-primary" /> Demand Outlook · <span data-testid="brief-demand-scope" className="normal-case tracking-normal">{o.scope}</span></span>}>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="kpi-label">Forecast · next 30d</div>
          <div data-testid="brief-demand-next30" className="font-mono-tabular text-xl font-semibold mt-1">{formatNumber(o.next30, { compact: true })} u</div>
        </div>
        <div>
          <div className="kpi-label">Actual · last 30d</div>
          <div data-testid="brief-demand-last30" className="font-mono-tabular text-xl font-semibold mt-1">{formatNumber(o.last30, { compact: true })} u</div>
          <div className={cn("text-[11px] font-mono-tabular inline-flex items-center gap-1 mt-0.5", up ? "text-success" : "text-warning")}>
            {up ? <TrendingUp size={10} /> : <TrendingDown size={10} />}{up ? "+" : ""}{(o.delta * 100).toFixed(1)}% next vs last
          </div>
        </div>
        <div>
          <div className="kpi-label">Forecast accuracy</div>
          <div data-testid="brief-demand-accuracy" className="font-mono-tabular text-xl font-semibold mt-1">{(o.accuracy * 100).toFixed(1)}%</div>
          <div className="mt-1"><OpenLink to="/forecast" label="Open forecasts" testid="brief-open-forecast" /></div>
        </div>
      </div>
    </Tile>
  );
}
